import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const SEX_OPTIONS = ['Colt', 'Filly', 'Gelding', 'Ridgling', 'Colt (deceased)', 'Filly (deceased)'];
const EMPTY = { foal_year: '', foal_name: '', linked_horse_id: '', sire_name: '', foal_sex: '', color: '', notes: '' };

export default function ProduceRecordFormDialog({ open, onClose, onSuccess, horse, record, offspring = [] }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (record) {
      setForm({
        foal_year: record.foal_year ?? '',
        foal_name: record.foal_name || '',
        linked_horse_id: record.linked_horse_id || '',
        sire_name: record.sire_name || '',
        foal_sex: record.foal_sex || '',
        color: record.color || '',
        notes: record.notes || '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, record]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target ? e.target.value : e }));

  // When a linked horse is selected, auto-populate fields from that horse's data
  const handleLinkedHorseChange = (horseId) => {
    const foal = offspring.find(o => o.id === horseId);
    if (foal) {
      const birthYear = foal.birthdate
        ? (String(foal.birthdate).slice(0, 4) || '')
        : '';
      setForm(f => ({
        ...f,
        linked_horse_id: horseId,
        foal_name: foal.system_name || foal.name || f.foal_name,
        sire_name: foal.sire_name || f.sire_name,
        foal_year: birthYear || f.foal_year,
        foal_sex: foal.gender === 'male' ? 'Colt' : foal.gender === 'female' ? 'Filly' : f.foal_sex,
        color: foal.color || f.color,
      }));
    } else {
      setForm(f => ({ ...f, linked_horse_id: horseId === '_none' ? '' : horseId }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, horse_id: horse.id };
      if (payload.foal_year) payload.foal_year = parseInt(payload.foal_year, 10);
      if (record) {
        await api.put(`/breeding-seasons/produce-records/${record.id}`, payload);
        toast.success('Produce record updated');
      } else {
        await api.post('/breeding-seasons/produce-records', payload);
        toast.success('Produce record added');
      }
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save produce record');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!record) return;
    setDeleting(true);
    try {
      await api.delete(`/breeding-seasons/produce-records/${record.id}`);
      toast.success('Produce record deleted');
      onSuccess();
      onClose();
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{record ? 'Edit Produce Record' : 'Add Produce Record'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Linked foal dropdown */}
          {offspring.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs">Link to Horse in System</Label>
              <Select
                value={form.linked_horse_id || '_none'}
                onValueChange={handleLinkedHorseChange}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select foal (auto-fills fields)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— None / manual entry —</SelectItem>
                  {offspring.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.system_name || o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Foal Name + Year on same row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Foal Name</Label>
              <Input className="h-8 text-sm" value={form.foal_name} onChange={set('foal_name')} placeholder="e.g. Belly Bean" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Foal Year</Label>
              <Input className="h-8 text-sm" type="number" min="1900" max="2100" value={form.foal_year} onChange={set('foal_year')} placeholder="e.g. 2024" />
            </div>
          </div>

          {/* Sire + Sex on same row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Sire</Label>
              <Input className="h-8 text-sm" value={form.sire_name} onChange={set('sire_name')} placeholder="Sire name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Sex</Label>
              <Select value={form.foal_sex || '_none'} onValueChange={v => setForm(f => ({ ...f, foal_sex: v === '_none' ? '' : v }))}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {SEX_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Color */}
          <div className="space-y-1.5">
            <Label className="text-xs">Color</Label>
            <Input className="h-8 text-sm" value={form.color} onChange={set('color')} placeholder="e.g. Bay" />
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes} onChange={set('notes')} placeholder="Race record, sold info, etc." rows={2} className="text-sm" />
          </div>
        </div>

        <DialogFooter>
          <div className="flex justify-between w-full">
            {record ? (
              <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600 hover:bg-red-50"
                onClick={handleDelete} disabled={deleting} title="Delete record">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-primary hover:bg-primary/90 text-white" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {record ? 'Save Changes' : 'Add Record'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
